import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { Fragment } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { Rewards } from '#src/components/missions/Rewards/index.tsx';

import type { TutoRewards_thresholdStreakTask } from './__generated__/index.graphql.ts';

const Root = styled(ButtonBase)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &:not(.active) {
    opacity: 0.5;
  }
`;

const LineThroughLabelS = styled(LabelS)`
  text-decoration: line-through;
`;

type Props = {
  thresholdStreakTask: TutoRewards_thresholdStreakTask;
  displayIndex?: number;
  setDisplayIndex?: (score: number) => void;
  isPlaceholder?: boolean;
};

export const TutoRewards = ({
  thresholdStreakTask,
  displayIndex,
  setDisplayIndex,
  isPlaceholder,
}: Props) => {
  const isMobile = useIsMobile();
  const { thresholds, progress, currentThresholdStatus, rarity } =
    thresholdStreakTask;

  return (
    <Scrollable itemToDisplay={isMobile ? 3 : 5} overhang>
      {thresholds.map(
        (
          { rewardConfigs, score, originalRewardConfigs, originalScore },
          index
        ) => {
          const isLowered = !!originalScore;
          const isBoosted = !!originalRewardConfigs?.length;
          const liveScore = currentThresholdStatus.liveScore || 0;
          const showLock = index > progress;
          const showCurrent = index === progress && liveScore < score;
          const showSuccess =
            index < progress || (index === progress && liveScore >= score);

          const color = (() => {
            if (isPlaceholder) return 'var(--c-rewards-orange)';
            if (showLock) return 'var(--c-nd-500)';
            if (showSuccess) return 'var(--c-onBase-success)';
            return 'var(--c-rewards-orange)';
          })();

          const isShardReward = rewardConfigs.every(
            isTypeFn('CardShardRewardConfig')
          );
          const rewards = isShardReward ? (
            <AnyRewardConfig
              anyRewardConfig={rewardConfigs[0]}
              variant={Variant.TEXT}
              rarity={rarity}
            />
          ) : (
            <LabelM color="var(--c-green-400)" bold>
              <Rewards
                rewardConfigs={rewardConfigs}
                variant="VALUE_ONLY"
                rarity={rarity}
                clickable={!setDisplayIndex}
              />
            </LabelM>
          );

          const active = index === (displayIndex ?? progress) || isPlaceholder;

          return (
            // oxlint-disable-next-line react/no-array-index-key
            <Fragment key={index}>
              <ConditionalWrapper
                Wrapper={Root}
                wrap={!!setDisplayIndex}
                props={{
                  onClick: () => setDisplayIndex?.(index),
                  className: classNames({
                    active,
                  }),
                }}
              >
                <Vertical center>
                  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                    {isPlaceholder ? (
                      <text
                        x="49%"
                        y="53%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="var(--c-rewards-orange)"
                        fontSize="12"
                        fontWeight="bold"
                      >
                        {index + 1}
                      </text>
                    ) : (
                      <>
                        {!!showLock && (
                          <g transform="translate(3 3)">
                            <path
                              d="M7.91016 11.4226H15.1403V16.8452H7.91016V11.4226Z"
                              stroke={color}
                              strokeWidth="1"
                            />
                            <path
                              d="M13.9354 11.4227V9.61514C13.9354 8.2841 12.8563 7.20508 11.5253 7.20508V7.20508C10.1943 7.20508 9.11523 8.2841 9.11523 9.61513V11.4227"
                              stroke={color}
                              strokeWidth="1"
                            />
                            <path
                              d="M10.3201 14.1339H12.7301"
                              stroke={color}
                              strokeWidth="1"
                            />
                          </g>
                        )}
                        {!!showCurrent && (
                          <g transform="translate(3 4)">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M11.5 15.8C14.1509 15.8 16.3 13.651 16.3 11C16.3 8.34906 14.1509 6.20002 11.5 6.20002C8.84898 6.20002 6.69995 8.34906 6.69995 11C6.69995 13.651 8.84898 15.8 11.5 15.8ZM11.4994 13.8994C13.101 13.8994 14.3994 12.6011 14.3994 10.9994C14.3994 9.39781 13.101 8.09944 11.4994 8.09944C9.89774 8.09944 8.59937 9.39781 8.59937 10.9994C8.59937 12.6011 9.89774 13.8994 11.4994 13.8994Z"
                              fill={color}
                            />
                            <path
                              d="M12.3007 7.80042L12.3007 4.60041H10.7007L10.7007 7.80042H12.3007Z"
                              fill={color}
                            />
                            <path
                              d="M5.10034 10.2008L8.30034 10.2008V11.8008H5.10034V10.2008Z"
                              fill={color}
                            />
                            <path
                              d="M17.9 10.2008L14.7 10.2008V11.8008H17.9L17.9 10.2008Z"
                              fill={color}
                            />
                            <path
                              d="M12.3007 17.4L12.3007 14.2H10.7007L10.7007 17.4L12.3007 17.4Z"
                              fill={color}
                            />
                          </g>
                        )}
                        {!!showSuccess && (
                          <g transform="translate(8 8)">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1.26541 8.23034C1.22081 11.4213 4.74414 12.5 4.74414 12.5C2.55879 9.80337 5.41312 9.44382 5.45772 7.33146C5.85911 7.46629 5.90371 8.72472 5.90371 8.72472C6.88489 8.23034 6.92949 5.98315 6.92949 5.98315C9.51623 8.45506 8.08907 12.4101 8.08907 12.4101C11.7462 11.6011 11.7016 7.87079 11.7016 7.87079C11.657 4.18539 8.80265 2.02809 8.98105 2.6573C9.20404 3.28652 8.75805 3.91573 8.75805 3.91573C7.91067 0.544944 5.41312 0.5 5.41312 0.5C5.76992 3.24157 3.98595 4.85955 3.98595 4.85955C2.82638 2.56742 2.91558 3.55618 2.69258 4.94944C0.997819 6.88202 1.26541 8.23034 1.26541 8.23034Z"
                              fill="url(#paint0_linear_18752_38240)"
                            />
                            <defs>
                              <linearGradient
                                id="paint0_linear_18752_38240"
                                x1="10.7115"
                                y1="14"
                                x2="4.93033"
                                y2="7.25934"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#FEF314" />
                                <stop offset="1" stopColor="#FF4E22" />
                              </linearGradient>
                            </defs>
                          </g>
                        )}
                      </>
                    )}
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.59493 3.97351V4.33595H3.83558V10.095H3.47314V3.97351H9.59493ZM3.69967 4.20003H9.45902V4.10942H3.60906V9.95904H3.69967V4.20003ZM19.4051 4.33595V3.97351H25.5262V10.095H25.1638V4.33595H19.4051ZM25.2997 9.95904H25.3903V4.10942H19.541V4.20003H25.2997V9.95904ZM19.4051 25.6642H25.1638V19.9051H25.5262V26.0266H19.4051V25.6642ZM25.2997 20.041V25.8001H19.541V25.8907H25.3903V20.041H25.2997ZM3.83558 19.9051V25.6642H9.59493V26.0266H3.47314V19.9051H3.83558ZM9.45902 25.8001H3.69967V20.041H3.60906V25.8907H9.45902V25.8001Z"
                      fill={color}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.94538 3H2.59061H2.5V3.09061V9.44577H2.59061V3.09061H8.94538V3ZM20.0543 3.09061V3H26.4094H26.5V3.09061V9.44577H26.4094V3.09061H20.0543ZM20.0543 26.9094H26.4094V20.5547H26.5V26.9094V27H26.4094H20.0543V26.9094ZM2.59061 20.5547V26.9094H8.94538V27H2.59061H2.5V26.9094V20.5547H2.59061Z"
                      fill={color}
                    />
                  </svg>
                  <Vertical gap={0.5} center>
                    <Horizontal gap={0.5}>
                      {isLowered ? (
                        <>
                          <LabelM
                            className="text-gradient-color"
                            style={
                              {
                                '--text-gradient-color':
                                  'linear-gradient(323deg, #D49E3C 14.3%, #FF4E22 85.7%)',
                              } as CSSProperties
                            }
                            bold
                          >
                            {score}
                          </LabelM>
                          <LineThroughLabelS color="var(--c-nd-600)">
                            {originalScore}
                          </LineThroughLabelS>
                        </>
                      ) : (
                        <LabelM bold>{score}</LabelM>
                      )}
                    </Horizontal>
                    <Horizontal gap={0.5}>
                      {!!isBoosted && (
                        <>
                          {rewards}
                          <LineThroughLabelS color="var(--c-nd-600)">
                            <Rewards
                              rewardConfigs={originalRewardConfigs}
                              variant="VALUE_ONLY"
                              rarity={rarity}
                              clickable={!setDisplayIndex}
                            />
                          </LineThroughLabelS>
                        </>
                      )}
                      {!isBoosted && rewards}
                    </Horizontal>
                  </Vertical>
                </Vertical>
              </ConditionalWrapper>
            </Fragment>
          );
        }
      )}
    </Scrollable>
  );
};

TutoRewards.fragments = {
  thresholdStreakTask: gql`
    fragment TutoRewards_thresholdStreakTask on ThresholdsStreakTask {
      id
      progress
      rarity
      currentThresholdStatus {
        liveScore
      }
      thresholds {
        score
        originalScore
        rewardConfigs {
          id
          ...Rewards_anyRewardConfig
        }
        originalRewardConfigs {
          id
          ...Rewards_anyRewardConfig
        }
      }
    }
    ${Rewards.fragments.anyRewardConfig}
  ` as TypedDocumentNode<TutoRewards_thresholdStreakTask>,
};
